import { useState } from 'react'
import { WhatIfModal } from '@/components/whatIf/WhatIfModal'
import { ProfitRecommendationModal } from './ProfitRecommendationModal'
import { formatMoney, formatPct } from '@/lib/format'
import type { ProfitTakingPosition, ProfitTriggerSeverity } from '@/types/profitTaking'

const SEVERITY_STYLE: Record<ProfitTriggerSeverity, { dot: string; label: string; text: string }> =
  {
    info: { dot: 'bg-faint', label: 'INFO', text: 'text-muted' },
    warning: { dot: 'bg-bernstein', label: 'WARN', text: 'text-bernstein' },
    critical: { dot: 'bg-loss', label: 'CRIT', text: 'text-loss' },
  }

interface ProfitTakingItemProps {
  position: ProfitTakingPosition
  compact?: boolean
}

/**
 * Eine Zeile in der Profit-Taking-Liste mit Position-Metriken + Trigger-Stack.
 * `compact` versteckt Detail-Body und zeigt nur Title + höchste Severity
 * (Dashboard-Widget).
 */
export function ProfitTakingItem({ position, compact = false }: ProfitTakingItemProps) {
  const [whatIfOpen, setWhatIfOpen] = useState(false)
  const [recOpen, setRecOpen] = useState(false)

  // Höchste Severity bestimmt den Border-Akzent.
  const topSeverity: ProfitTriggerSeverity =
    position.triggers.find((t) => t.severity === 'critical')?.severity ??
    position.triggers.find((t) => t.severity === 'warning')?.severity ??
    'info'
  const topStyle = SEVERITY_STYLE[topSeverity]

  return (
    <div className="hairline-b flex flex-col gap-3 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <span className={`tracked-caps text-[10px] ${topStyle.text}`}>{topStyle.label}</span>
          <p className="text-sm text-ink">{position.name}</p>
        </div>
        <p className="font-mono text-xs text-muted">
          {formatPct(position.gain_pct)}
          {' · '}
          {formatMoney(position.position_value, position.currency)}
        </p>
      </div>

      {!compact && (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 font-mono text-[11px] text-muted">
          <div className="flex justify-between">
            <dt>Einstand</dt>
            <dd className="text-ink">{formatMoney(position.avg_cost, position.currency)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Aktuell</dt>
            <dd className="text-ink">{formatMoney(position.current_price, position.currency)}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Stück gesamt</dt>
            <dd className="text-ink">{position.total_quantity}</dd>
          </div>
          <div className="flex justify-between">
            <dt>steuerfrei</dt>
            <dd className="text-ink">{position.tax_free_quantity}</dd>
          </div>
        </dl>
      )}

      <ul className="flex flex-col gap-2">
        {position.triggers.map((trigger) => {
          const style = SEVERITY_STYLE[trigger.severity]
          return (
            <li
              key={trigger.type}
              className="flex items-start gap-3 border border-hairline bg-void px-3 py-2"
            >
              <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${style.dot}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <p className={`tracked-caps text-[10px] ${style.text}`}>{trigger.title}</p>
                {!compact && (
                  <p className="mt-1 font-mono text-[11px] text-muted">{trigger.body}</p>
                )}
              </div>
            </li>
          )
        })}
      </ul>

      {!compact && (
        <div className="flex gap-3 self-end">
          {position.recommendation && (
            <button
              type="button"
              onClick={() => setRecOpen(true)}
              className="text-xs text-muted hover:text-ink"
            >
              Empfehlung ansehen
            </button>
          )}
          <button
            type="button"
            onClick={() => setWhatIfOpen(true)}
            className="text-xs text-muted hover:text-ink"
          >
            What-if simulieren
          </button>
        </div>
      )}
      <WhatIfModal
        open={whatIfOpen}
        onClose={() => setWhatIfOpen(false)}
        initialPositionId={position.position_id}
      />
      <ProfitRecommendationModal
        open={recOpen}
        onClose={() => setRecOpen(false)}
        position={position}
      />
    </div>
  )
}
